import { ValueObject } from '../shared/ValueObject';
import { ValidationError } from '../errors/ValidationError';

export type PanelStatusValue = 'active' | 'stopped' | 'warning';

/**
 * 制御盤ステータス値オブジェクト
 *
 * 不変条件:
 * - 'active', 'stopped', 'warning' のいずれか
 */
export class PanelStatus extends ValueObject<PanelStatusValue> {
  private static readonly VALID_STATUSES: readonly PanelStatusValue[] = [
    'active',
    'stopped',
    'warning'
  ];

  private constructor(value: PanelStatusValue) {
    super(value);
  }

  /**
   * PanelStatusを生成
   * @throws {ValidationError} 不正な値の場合
   */
  public static create(value: string): PanelStatus {
    PanelStatus.validate(value);
    return new PanelStatus(value as PanelStatusValue);
  }

  /**
   * ファクトリメソッド: active状態を生成
   */
  public static createActive(): PanelStatus {
    return new PanelStatus('active');
  }

  /**
   * ファクトリメソッド: stopped状態を生成
   */
  public static createStopped(): PanelStatus {
    return new PanelStatus('stopped');
  }

  /**
   * ファクトリメソッド: warning状態を生成
   */
  public static createWarning(): PanelStatus {
    return new PanelStatus('warning');
  }

  /**
   * 文字列から生成（createのエイリアス）
   */
  public static fromString(value: string): PanelStatus {
    return PanelStatus.create(value);
  }

  private static validate(value: string): void {
    if (!PanelStatus.VALID_STATUSES.includes(value as PanelStatusValue)) {
      throw new ValidationError(
        `無効なステータス値: ${value}. 有効な値: ${PanelStatus.VALID_STATUSES.join(', ')}`
      );
    }
  }

  /**
   * 稼働中かどうかを判定
   */
  public isActive(): boolean {
    return this.value === 'active';
  }

  /**
   * 停止中かどうかを判定
   */
  public isStopped(): boolean {
    return this.value === 'stopped';
  }

  /**
   * 警告状態かどうかを判定
   */
  public isWarning(): boolean {
    return this.value === 'warning';
  }

  public toString(): string {
    return this.value;
  }

  public toJSON(): string {
    return this.value;
  }

  /**
   * 日本語表示文字列を取得
   */
  public toDisplayString(): string {
    switch (this.value) {
      case 'active':
        return '稼働中';
      case 'stopped':
        return '停止';
      case 'warning':
        return '警告';
    }
  }

  /**
   * インデックスアクセス用のキー文字列を取得
   * オブジェクトのキーとして使用する場合に使用
   *
   * @example
   * const config = STATUS_CONFIG[status.toKey()];
   */
  public toKey(): PanelStatusValue {
    return this.value;
  }

  /**
   * 他のPanelStatusと等しいかを判定
   */
  public equals(other: PanelStatus | null | undefined): boolean {
    if (!(other instanceof PanelStatus)) {
      return false;
    }
    return this.value === other.value;
  }

}